// Per-type profile fields — food & drink and bike mechanics (T57 WP4, pilot
// scope per decision 061).
//
// One model, two consumers: the provider's panel on #/edit and the operator's
// "what's missing" column on #/admin. Keeping the field list in one place is the
// point — a completeness column that disagrees with the form it grades is worse
// than no column at all.
//
// DOCTRINE: everything here is optional. Screen one (name, type, email) already
// published the profile; these fields make it better, never valid. Nothing in
// this module may block a save.

export type PerTypeGroup = 'food' | 'mechanic';

export interface PerTypeProfile {
  business_type: string | null;
  is_active: boolean | null;
  // food
  cuisine_types: unknown;
  dietary_options: unknown;
  reservation_required: boolean | null;
  outdoor_seating: boolean | null;
  capacity: number | null;
  price_range: string | null;
  booking_platform: string | null;
  external_booking_url: string | null;
  bokun_activity_id: string | null;
  viator_product_code: string | null;
  thefork_restaurant_id: string | null;
  booking_com_property_id: string | null;
  // mechanic
  mechanic_service_type: string | null;
  mobile_service: boolean | null;
  emergency_callout: boolean | null;
  response_time: string | null;
  coverage_zones: unknown;
  services_offered: unknown;
}

export const PER_TYPE_COLUMNS = [
  'business_type', 'is_active',
  'cuisine_types', 'dietary_options', 'reservation_required', 'outdoor_seating',
  'capacity', 'price_range', 'booking_platform', 'external_booking_url',
  'bokun_activity_id', 'viator_product_code', 'thefork_restaurant_id',
  'booking_com_property_id',
  'mechanic_service_type', 'mobile_service', 'emergency_callout', 'response_time',
  'coverage_zones', 'services_offered',
].join(', ');

const FOOD_TYPES = new Set(['restaurant', 'bar', 'food_drink']);
const MECHANIC_TYPES = new Set(['mechanic', 'mobile_mechanic']);

export function groupFor(businessType: string | null | undefined): PerTypeGroup | null {
  if (!businessType) return null;
  if (FOOD_TYPES.has(businessType)) return 'food';
  if (MECHANIC_TYPES.has(businessType)) return 'mechanic';
  return null;
}

/** The 13 canonical booking platforms are constrained by a DB CHECK; mirror it. */
export const BOOKING_PLATFORMS: ReadonlyArray<readonly [string, string]> = [
  ['', 'Not set'],
  ['thefork', 'TheFork'],
  ['bokun', 'Bokun'],
  ['viator', 'Viator'],
  ['booking_com', 'Booking.com'],
  ['stripe', 'Stripe'],
  ['external', 'My own website'],
];

/**
 * Which id column a platform stores its identifier in. `null` = url only.
 *
 * OPERATOR-ONLY since John's ruling (2026-09-05). The provider picks the platform
 * and gives their own link; the ID that names a record in someone else's commerce
 * system is set by the operator in #/admin, because it is what makes the pipeline
 * say "[Direct booking available]" to a traveller. The map stays here because
 * #/admin uses it — the provider form no longer renders the field.
 */
export const PLATFORM_ID_COLUMN: Record<string, keyof PerTypeProfile | null> = {
  thefork: 'thefork_restaurant_id',
  bokun: 'bokun_activity_id',
  viator: 'viator_product_code',
  booking_com: 'booking_com_property_id',
  stripe: null,
  external: null,
};

export const CUISINES = [
  'Tuscan', 'Italian', 'Pizza', 'Seafood', 'Steakhouse', 'Vegetarian',
  'Street food', 'Bakery', 'Gelato', 'Wine bar', 'Cocktails', 'Coffee',
] as const;

export const DIETARY = [
  ['vegetarian', 'Vegetarian'],
  ['vegan', 'Vegan'],
  ['gluten_free', 'Gluten free'],
  ['dairy_free', 'Dairy free'],
  ['halal', 'Halal'],
  ['nut_free', 'Nut free'],
] as const;

export const MECHANIC_SERVICE_TYPES: ReadonlyArray<readonly [string, string]> = [
  ['', 'Not available for callouts'],
  ['bike_shop', 'Bike shop — riders come to me'],
  ['mobile_mechanic', 'Mobile mechanic — I go to the rider'],
];

export const SERVICES_OFFERED = [
  'Puncture repair', 'Brake adjustment', 'Gear tuning', 'Chain replacement',
  'Wheel truing', 'E-bike diagnostics', 'Emergency callout',
] as const;

export function toStringList(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
}

/** A coverage zone. Only the radius shape is offered today — see REPORT NEEDS-JOHN. */
export interface CoverageRadius {
  kind: 'radius';
  lat: number;
  lng: number;
  radius_km: number;
}

export function readCoverageRadius(raw: unknown): CoverageRadius | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const first = raw[0] as Partial<CoverageRadius> | undefined;
  if (!first || first.kind !== 'radius') return null;
  const { lat, lng, radius_km: km } = first;
  if (typeof lat !== 'number' || typeof lng !== 'number' || typeof km !== 'number') return null;
  return { kind: 'radius', lat, lng, radius_km: km };
}

export interface MissingField {
  key: string;
  label: string;
}

/**
 * What this profile still lacks, for its type. Ordered by how much it helps a
 * traveller, so #/admin's first entry is the most useful thing to chase.
 *
 * Deliberately NOT a score. "60% complete" invites completing the cheap fields;
 * a named list invites filling the one that matters.
 */
export function missingFor(p: PerTypeProfile): MissingField[] {
  const out: MissingField[] = [];
  const group = groupFor(p.business_type);

  if (group === 'food') {
    if (toStringList(p.cuisine_types).length === 0) out.push({ key: 'cuisine', label: 'Cuisine' });
    if (!p.price_range) out.push({ key: 'price_range', label: 'Price range' });
    if (toStringList(p.dietary_options).length === 0) out.push({ key: 'dietary', label: 'Dietary options' });
    if (p.reservation_required === null) out.push({ key: 'reservation', label: 'Reservations' });
    if (p.outdoor_seating === null) out.push({ key: 'outdoor', label: 'Outdoor seating' });
    if (p.capacity === null) out.push({ key: 'capacity', label: 'Capacity' });
    // The provider can only supply the URL — booking_platform is operator-only —
    // so grade them on the half they control. #/admin still shows the platform
    // in its own Booking column.
    if (!p.external_booking_url) out.push({ key: 'booking', label: 'Booking link' });
  }

  if (group === 'mechanic') {
    // The SOS toggle first: it is the only field that changes whether a rider in
    // trouble can reach this provider at all.
    if (!p.mechanic_service_type) out.push({ key: 'sos', label: 'SOS availability' });
    if (!p.response_time) out.push({ key: 'response_time', label: 'Response time' });
    if (readCoverageRadius(p.coverage_zones) === null) {
      out.push({ key: 'coverage', label: 'Coverage area' });
    }
    if (toStringList(p.services_offered).length === 0) {
      out.push({ key: 'services', label: 'Services offered' });
    }
    if (p.emergency_callout === null) out.push({ key: 'emergency', label: 'Emergency callout' });
  }

  return out;
}

/**
 * SOS eligibility, exactly as the backend computes it:
 * `mechanic_service_type IS NOT NULL AND is_active IS TRUE`.
 *
 * The provider owns the first half. `is_active` is operator-only by design — it
 * gates visibility everywhere — so a provider can offer themselves for SOS but
 * cannot put a suspended listing back on the map.
 */
export function sosEligible(p: PerTypeProfile): boolean {
  return p.mechanic_service_type !== null && p.is_active === true;
}
